import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home, LogOut, Package, User, RefreshCw, AlertCircle,
  MapPin, Phone, Mail, CreditCard, Globe, Calendar,
  ChevronDown, ScanLine, Truck, AlertTriangle,
  StickyNote, CheckCircle2, Loader2, PackageX,
  Search, X as XIcon, Clock, Lock, Copy, Building2,
  DollarSign, Eye, EyeOff, ChevronRight, Receipt, Info, Filter, Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuth() {
  try { return JSON.parse(localStorage.getItem("kyo_auth") || "null"); }
  catch { return null; }
}
function getAuthHeaders() {
  const auth = getAuth();
  return auth?.token ? { Authorization: `Bearer ${auth.token}` } : {};
}
function getCustomerNumber() {
  return localStorage.getItem("kyo_customer_number") || null;
}
function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

/* ── localStorage tracking history helpers ── */
function readLocalHistory() {
  try { return JSON.parse(localStorage.getItem("kyo_tracking_history") || "[]"); }
  catch { return []; }
}
function syncApiTNsToLocalHistory(pkgList) {
  const apiTNs = pkgList
    .map(p => p.tracking_number || p.trackingNumber || p.number)
    .filter(Boolean);
  if (!apiTNs.length) return;
  const existing = readLocalHistory();
  const merged = [...new Set([...apiTNs, ...existing])].slice(0, 10);
  localStorage.setItem("kyo_tracking_history", JSON.stringify(merged));
}

/* ── Status badge ── */
function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  let cls = "bg-gray-100 text-gray-600 border-gray-200";
  if (s.includes("deliv") || s.includes("complete")) cls = "bg-green-50 text-green-700 border-green-100";
  else if (s.includes("transit") || s.includes("ship") || s.includes("dispatch")) cls = "bg-blue-50 text-blue-700 border-blue-100";
  else if (s.includes("pend") || s.includes("process")) cls = "bg-yellow-50 text-yellow-700 border-yellow-100";
  else if (s.includes("hold") || s.includes("custom")) cls = "bg-orange-50 text-orange-700 border-orange-100";
  else if (s.includes("cancel")) cls = "bg-red-50 text-red-600 border-red-100";
  return (
    <span className={`text-xs px-2.5 py-0.5 rounded-full border font-medium capitalize ${cls}`}>
      {status || "Unknown"}
    </span>
  );
}

/* ── Account detail row ── */
function DetailRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={13} className="text-gray-400" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-sm text-gray-800 font-medium mt-0.5 break-words">{value}</p>
      </div>
    </div>
  );
}

/* ── Inline tracking timeline helpers ── */
const TRACK_EVENT_META = {
  scan:            { Icon: ScanLine,       color: "text-blue-600",    ring: "border-blue-200 bg-blue-50" },
  checkpoint:      { Icon: MapPin,         color: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50" },
  out_for_delivery:{ Icon: Truck,          color: "text-amber-600",   ring: "border-amber-200 bg-amber-50" },
  exception:       { Icon: AlertTriangle,  color: "text-red-600",     ring: "border-red-200 bg-red-50" },
  note:            { Icon: StickyNote,     color: "text-zinc-600",    ring: "border-zinc-200 bg-zinc-50" },
};

function buildTrail(data) {
  const trail = [];
  if (data.created_at) trail.push({ id: "__start", Icon: Package, color: "text-zinc-500", ring: "border-zinc-200 bg-zinc-100", label: "Shipped", notes: "Package created in courier system", created_at: data.created_at, branch_name: null });
  (data.events || []).forEach((e) => {
    const m = TRACK_EVENT_META[e.event_type] || TRACK_EVENT_META.scan;
    trail.push({ id: e.id, Icon: m.Icon, color: m.color, ring: m.ring, label: m.label || e.event_type, notes: e.notes, created_at: e.created_at, branch_name: e.branch_name });
  });
  if ((data.status || "").toLowerCase() === "delivered") trail.push({ id: "__delivered", Icon: CheckCircle2, color: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50", label: "Delivered", notes: "Package successfully delivered", created_at: data.updated_at, branch_name: null });
  return trail;
}

function fmtTs(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleString(); } catch { return ""; }
}

function InlineTimeline({ tn }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    axios.get(`${API}/track/${tn}`)
      .then(({ data }) => { if (!cancelled) setState({ loading: false, error: null, data }); })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.status === 404
          ? "Tracking details not available for this package yet."
          : "Could not load tracking timeline.";
        setState({ loading: false, error: msg, data: null });
      });
    return () => { cancelled = true; };
  }, [tn]);

  if (state.loading) return (
    <div className="flex items-center gap-2 text-gray-400 py-4 px-1 text-sm">
      <Loader2 size={15} className="animate-spin text-[#ED2E24]" />
      Loading tracking timeline…
    </div>
  );

  if (state.error) return (
    <div className="flex items-center gap-2 text-red-500 text-sm py-3 px-1">
      <PackageX size={15} className="shrink-0" />{state.error}
    </div>
  );

  const d = state.data;
  const trail = buildTrail(d);

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      {/* mini details row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {d.recipient_name && <div><p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Recipient</p><p className="text-[12px] text-gray-700 font-medium">{d.recipient_name}</p></div>}
        {d.weight_kg      && <div><p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Weight</p><p className="text-[12px] text-gray-700 font-medium">{Number(d.weight_kg).toFixed(2)} kg</p></div>}
        {d.shipment_method && <div><p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Method</p><p className="text-[12px] text-gray-700 font-medium">{d.shipment_method}</p></div>}
        {d.recipient_address && <div className="col-span-2 sm:col-span-1"><p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Deliver to</p><p className="text-[12px] text-gray-700 font-medium leading-snug">{d.recipient_address}</p></div>}
      </div>

      {/* timeline */}
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={13} className="text-[#ED2E24]" />
        <span className="text-[12px] font-semibold text-gray-600 uppercase tracking-wide">Journey</span>
        <span className="text-[11px] text-gray-400">{trail.length} event{trail.length !== 1 ? "s" : ""}</span>
      </div>

      {trail.length === 0 ? (
        <p className="text-[12px] text-gray-400 italic border border-dashed border-gray-200 rounded-lg p-4 text-center">No scan events yet. Your package journey will appear here.</p>
      ) : (
        <ol className="relative space-y-0">
          {trail.map((ev, idx) => (
            <li key={ev.id || idx} className="flex gap-3 pb-4 last:pb-0 relative">
              {idx < trail.length - 1 && (
                <span className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-200" aria-hidden="true" />
              )}
              <span className={`shrink-0 w-8 h-8 rounded-full border flex items-center justify-center ${ev.ring}`}>
                <ev.Icon size={14} className={ev.color} />
              </span>
              <div className="pt-0.5 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-gray-800">{ev.label}</span>
                  {ev.branch_name && <span className="text-[11px] text-gray-400">· {ev.branch_name}</span>}
                </div>
                {ev.notes && <p className="text-[12px] text-gray-500 mt-0.5 leading-snug">{ev.notes}</p>}
                <p className="text-[11px] text-gray-400 mt-0.5">{fmtTs(ev.created_at)}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* ── Quick-Track inline panel ── */
function QuickTrack() {
  const [input, setInput] = useState("");
  const [state, setState] = useState({ loading: false, error: null, data: null, tn: null });

  const track = async (e) => {
    e.preventDefault();
    const tn = input.trim();
    if (!tn) return;
    setState({ loading: true, error: null, data: null, tn });
    try {
      const { data } = await axios.get(`${API}/track/${encodeURIComponent(tn)}`);
      setState({ loading: false, error: null, data, tn });
    } catch (err) {
      const msg = err?.response?.status === 404
        ? `No package found for "${tn}". Please check the number.`
        : err?.response?.data?.detail || "Unable to track right now. Please try again.";
      setState({ loading: false, error: msg, data: null, tn });
    }
  };

  const clear = () => { setInput(""); setState({ loading: false, error: null, data: null, tn: null }); };

  return (
    <div className="mb-4" data-testid="quick-track-panel">
      <form onSubmit={track} className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Track a number e.g. KN123456789"
            className="w-full pl-8 pr-3 py-2.5 text-[13px] bg-white border border-gray-100 rounded-xl outline-none focus:border-[#ED2E24]/40 focus:ring-1 focus:ring-[#ED2E24]/20 transition-all placeholder:text-gray-300"
            data-testid="quick-track-input"
          />
        </div>
        <button
          type="submit"
          disabled={state.loading || !input.trim()}
          className="shrink-0 bg-[#ED2E24] hover:bg-[#c9231a] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2.5 rounded-xl transition-colors"
          data-testid="quick-track-btn"
        >
          {state.loading ? <Loader2 size={14} className="animate-spin" /> : "Track"}
        </button>
        {(state.data || state.error) && (
          <button
            type="button"
            onClick={clear}
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl border border-gray-100 text-gray-400 hover:text-gray-600 hover:border-gray-200 transition-colors"
            data-testid="quick-track-clear"
            title="Clear result"
          >
            <XIcon size={14} />
          </button>
        )}
      </form>

      {/* Result panel */}
      {(state.error || state.data) && (
        <div
          className={`mt-2 rounded-xl border ${state.error ? "border-red-100 bg-red-50" : "border-gray-100 bg-white"} overflow-hidden`}
          data-testid="quick-track-result"
        >
          {state.error ? (
            <div className="flex items-start gap-2 px-4 py-3 text-red-600 text-[13px]">
              <PackageX size={15} className="shrink-0 mt-0.5" />
              <span>{state.error}</span>
            </div>
          ) : (
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-[13px] font-bold text-[#ED2E24]">{state.tn}</span>
                {state.data?.status && (
                  <span className="text-[11px] bg-[#ED2E24]/10 text-[#ED2E24] font-medium px-2 py-0.5 rounded-full capitalize">
                    {state.data.status}
                  </span>
                )}
              </div>
              <InlineTimeline tn={state.tn} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Illustrated empty state ── */
function EmptyPackages({ onTrack }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 px-6 py-14 flex flex-col items-center text-center" data-testid="packages-empty-state">
      {/* Inline illustration */}
      <svg width="140" height="120" viewBox="0 0 140 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-6 opacity-90">
        {/* dotted path */}
        <path d="M20 90 Q70 30 120 90" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="5 4" fill="none"/>
        {/* ground shadow */}
        <ellipse cx="70" cy="105" rx="38" ry="7" fill="#f3f4f6"/>
        {/* box body */}
        <rect x="38" y="62" width="64" height="48" rx="5" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1.5"/>
        {/* box lid */}
        <path d="M38 72 L70 78 L102 72 L102 62 Q70 68 38 62 Z" fill="#f1f5f9" stroke="#e5e7eb" strokeWidth="1.5"/>
        {/* KYo red stripe on lid */}
        <path d="M54 65 L86 65" stroke="#ED2E24" strokeWidth="2.5" strokeLinecap="round"/>
        {/* tape cross */}
        <line x1="70" y1="62" x2="70" y2="110" stroke="#e2e8f0" strokeWidth="1.5"/>
        {/* left handle */}
        <path d="M55 62 Q55 54 63 54 Q71 54 71 62" stroke="#e2e8f0" strokeWidth="1.5" fill="none"/>
        {/* right handle */}
        <path d="M69 62 Q69 54 77 54 Q85 54 85 62" stroke="#e2e8f0" strokeWidth="1.5" fill="none"/>
        {/* question mark */}
        <text x="70" y="95" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#d1d5db" fontFamily="sans-serif">?</text>
        {/* sparkles */}
        <circle cx="22" cy="50" r="3" fill="#ED2E24" opacity="0.25"/>
        <circle cx="118" cy="48" r="2" fill="#ED2E24" opacity="0.2"/>
        <circle cx="28" cy="80" r="1.5" fill="#9ca3af" opacity="0.4"/>
        <circle cx="112" cy="78" r="2" fill="#9ca3af" opacity="0.35"/>
        {/* plane silhouette */}
        <g transform="translate(10 30) rotate(-20)" opacity="0.18">
          <path d="M0 8 L22 4 L32 6 L26 10 L20 9 L16 14 L11 14 L14 9 L4 10 Z" fill="#6b7280"/>
        </g>
      </svg>

      <h3 className="text-[16px] font-bold text-gray-800 mb-2">No packages on your account yet</h3>
      <p className="text-[13px] text-gray-400 max-w-[280px] leading-relaxed mb-6">
        Your shipments will show up here once you start sending with KYo. You can also track any package right now using your tracking number.
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        <button
          onClick={onTrack}
          className="bg-[#ED2E24] hover:bg-[#c9231a] text-white text-[13px] font-semibold px-5 py-2.5 rounded-full transition-colors shadow-sm"
          data-testid="empty-state-track-cta"
        >
          Track a Package
        </button>
        <a
          href="https://kyoown.com"
          target="_blank"
          rel="noreferrer"
          className="border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-800 text-[13px] font-medium px-5 py-2.5 rounded-full transition-colors"
          data-testid="empty-state-ship-cta"
        >
          Start Shipping
        </a>
      </div>
    </div>
  );
}

/* ── Recently-tracked card (local-only history item) ── */
function LocallyTrackedCard({ tn, expandedId, setExpandedId }) {
  const isExpanded = expandedId === `local:${tn}`;
  return (
    <div
      className={`bg-white rounded-xl border transition-all duration-200 ${isExpanded ? "border-blue-200 shadow-sm" : "border-gray-100 hover:border-gray-200"}`}
      data-testid={`local-package-card-${tn}`}
    >
      <button
        className="w-full text-left px-5 py-4 group"
        onClick={() => setExpandedId(isExpanded ? null : `local:${tn}`)}
        aria-expanded={isExpanded}
        data-testid={`local-expand-btn-${tn}`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className={`font-mono text-sm font-bold transition-colors ${isExpanded ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600"}`}>{tn}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Tracked locally — not yet linked to your account</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] bg-gray-100 text-gray-400 border border-gray-200 px-2 py-0.5 rounded-full font-medium">Recent search</span>
            <span className={`text-gray-300 transition-transform duration-200 ${isExpanded ? "rotate-180 text-blue-400" : ""}`}>
              <ChevronDown size={16} />
            </span>
          </div>
        </div>
      </button>
      {isExpanded && (
        <div className="px-5 pb-5" data-testid={`local-timeline-${tn}`}>
          <InlineTimeline tn={tn} />
        </div>
      )}
    </div>
  );
}

/* ── Skeleton loader ── */
function Skeleton({ lines = 4 }) {
  return (
    <div className="space-y-4 animate-pulse">
      {[...Array(lines)].map((_, i) => (
        <div key={i} className="flex gap-3">
          <div className="w-7 h-7 bg-gray-100 rounded-lg shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-2.5 bg-gray-100 rounded w-1/4" />
            <div className="h-4 bg-gray-50 rounded w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Package skeleton ── */
function PackageSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex justify-between">
            <div className="w-2/5 space-y-1.5">
              <div className="h-3 bg-gray-200 rounded" />
              <div className="h-3 bg-gray-100 rounded w-3/4" />
            </div>
            <div className="h-6 w-20 bg-gray-100 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Change Password Form ── */
function ChangePasswordForm({ onClose }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const up = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const toggleShow = (f) => setShow((p) => ({ ...p, [f]: !p[f] }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.current) { setError("Please enter your current password."); return; }
    if (form.next.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (form.next !== form.confirm) { setError("New passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: form.current, new_password: form.next },
        { headers: getAuthHeaders() }
      );
      setSuccess(true);
      setTimeout(onClose, 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not change password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="flex items-center gap-2 text-green-600 text-sm py-3 px-1" data-testid="change-password-success">
      <CheckCircle2 size={15} className="shrink-0" />
      Password changed successfully!
    </div>
  );

  const fields = [
    { key: "current", placeholder: "Current password" },
    { key: "next",    placeholder: "New password (min. 6 chars)" },
    { key: "confirm", placeholder: "Confirm new password" },
  ];

  return (
    <form onSubmit={submit} className="space-y-2.5 pt-1" data-testid="change-password-form">
      {fields.map(({ key, placeholder }) => (
        <div key={key} className="relative">
          <input
            type={show[key] ? "text" : "password"}
            value={form[key]}
            onChange={up(key)}
            placeholder={placeholder}
            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-[13px] pr-9 outline-none focus:border-gray-400 transition-colors"
          />
          <button
            type="button"
            onClick={() => toggleShow(key)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
          >
            {show[key] ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        </div>
      ))}
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1.5" data-testid="change-password-error">
          <AlertCircle size={11} className="shrink-0" />{error}
        </p>
      )}
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-[#ED2E24] hover:bg-[#c9231a] disabled:opacity-50 text-white text-[13px] font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5"
          data-testid="change-password-submit"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : "Update Password"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2.5 text-[13px] text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ── Warehouse shipping address card ── */
function WarehouseCard({ profile, warehouse, warehouseLoading, customerNumber }) {
  const [copied, setCopied] = useState(false);

  const name = profile?.name || profile?.full_name ||
    (profile?.first_name ? `${profile.first_name} ${profile.last_name || ""}`.trim() : null) || "Customer";
  const custNum = profile?.customer_number || customerNumber || "";

  /* Address resolution:
     1. Check top-level fields from /api/auth/me warehouse sub-object
     2. Fall back to hardcoded KYo Miami warehouse address (always correct for shipping) */
  const wh = warehouse?.warehouse || warehouse?.shipping_address || warehouse || {};
  const street  = wh.address_line1 || wh.street  || wh.address || "2033 W McNab Rd Suite S";
  const city    = wh.city    || "Pompano Beach";
  const state   = wh.state   || "FL";
  const zip     = wh.zip     || wh.postal_code || "33414";
  const country = (wh.country && wh.country !== "JM" && wh.country !== "Jamaica") ? wh.country : "USA";

  const fullAddress = `${name} — ${custNum}\n${street}\n${city}, ${state} ${zip}\n${country}`;

  const copy = () => {
    navigator.clipboard.writeText(fullAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-emerald-100 overflow-hidden mb-6" data-testid="warehouse-card">
      {/* Header */}
      <div className="bg-emerald-50 px-5 py-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
            <Building2 size={15} className="text-emerald-600" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">
            KYo Miami Warehouse · Air / Ocean Shipping
          </p>
        </div>
        <button
          onClick={copy}
          className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold px-3.5 py-2 rounded-lg transition-colors shrink-0"
          data-testid="copy-address-btn"
        >
          <Copy size={12} />
          {copied ? "Copied!" : "Copy full address"}
        </button>
      </div>

      {/* Instructions */}
      <div className="px-5 pt-3.5 pb-0">
        <p className="text-[13px] text-gray-500">
          Use this US address when shopping online. Always include your customer number on the second line so we route your packages to <strong>KYo</strong>.
        </p>
      </div>

      {/* Address box */}
      <div className="px-5 pt-3 pb-4">
        {warehouseLoading ? (
          <div className="bg-gray-50 rounded-xl p-4 animate-pulse space-y-2">
            <div className="h-4 bg-gray-200 rounded w-2/5" />
            <div className="h-3 bg-gray-100 rounded w-3/5" />
            <div className="h-3 bg-gray-100 rounded w-1/2" />
            <div className="h-3 bg-gray-100 rounded w-1/6" />
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl p-4 font-mono text-[13px] text-gray-700 leading-relaxed border border-gray-100 space-y-0.5">
            <p>
              <span className="font-bold text-gray-900">{name}</span>
              <span className="text-gray-400"> — </span>
              <span className="font-bold text-[#ED2E24]">{custNum}</span>
            </p>
            <p>{street}</p>
            <p>{city}, {state} {zip}</p>
            <p>{country}</p>
          </div>
        )}
        <p className="mt-3 text-[11px] text-gray-400">
          Ships via both <span className="font-semibold text-gray-500">Air</span> and <span className="font-semibold text-gray-500">Ocean</span>. Forgetting your customer number on a parcel delays delivery — copy the address above to keep it accurate.
        </p>
      </div>
    </div>
  );
}

/* Known KYo shipping rates — used when Kanal API returns empty for an account */
const KYO_FALLBACK_RATES = [
  { lbs: 1,  price: 600   },
  { lbs: 2,  price: 1000  },
  { lbs: 3,  price: 1400  },
  { lbs: 4,  price: 1750  },
  { lbs: 5,  price: 2100  },
  { lbs: 6,  price: 2450  },
  { lbs: 7,  price: 2800  },
  { lbs: 8,  price: 3150  },
  { lbs: 9,  price: 3500  },
  { lbs: 10, price: 3850  },
];

/* ── Shipping rates card ── */
function ShippingRatesCard({ rates, ratesLoading, ratesError }) {
  const [expanded, setExpanded] = useState(false);

  /* Normalise every plausible Kanal response shape into a flat array;
     fall back to hardcoded KYo rates when the API returns empty */
  const rawTiers = (() => {
    if (!rates) return [];
    if (Array.isArray(rates)) return rates;

    // All known named-array field candidates — including Kanal's confirmed 'rate_table' key
    const candidates = [
      rates.rate_table, rates.tiers, rates.rates, rates.data, rates.items, rates.results,
      rates.courier_rates, rates.rate_list, rates.rateList,
      rates.price_list, rates.priceList, rates.courier_rate,
      rates.shipping_rates, rates.payload, rates.response,
      rates.list, rates.rate_data, rates.courier_list, rates.body,
    ];
    for (const c of candidates) {
      if (Array.isArray(c) && c.length > 0) return c;
    }

    // If the response itself is an object whose values look like rate entries,
    // convert it: { "1": {weight:1,price:600}, ... } → [{weight:1,price:600}, ...]
    const vals = Object.values(rates);
    if (
      vals.length > 0 &&
      vals.every((v) => v && typeof v === "object" && !Array.isArray(v))
    ) {
      return vals;
    }

    // Deep scan — unwrap one more level
    for (const val of vals) {
      if (Array.isArray(val) && val.length > 0) return val;
      if (val && typeof val === "object") {
        for (const inner of Object.values(val)) {
          if (Array.isArray(inner) && inner.length > 0) return inner;
        }
      }
    }
    return [];
  })();
  const tiers = rawTiers.length > 0 ? rawTiers : KYO_FALLBACK_RATES;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mt-4" data-testid="shipping-rates-card">
      <div className="px-5 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
            <DollarSign size={15} className="text-[#ED2E24]" />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Shipping Rates</p>
            {!ratesLoading && tiers.length > 0 && (
              <p className="text-[13px] font-bold text-gray-800 mt-0.5">{tiers.length} weight tier{tiers.length !== 1 ? "s" : ""}</p>
            )}
            {!ratesLoading && tiers.length === 0 && !ratesError && (
              <p className="text-[12px] text-gray-400 mt-0.5">No tiers available</p>
            )}
          </div>
        </div>
        {tiers.length > 0 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 text-[12px] text-[#ED2E24] font-semibold hover:text-[#c9231a] transition-colors shrink-0"
            data-testid="view-price-list-btn"
          >
            View full price list
            <ChevronRight size={13} className={`transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
          </button>
        )}
      </div>

      {ratesLoading && (
        <div className="px-5 pb-4 animate-pulse space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-7 bg-gray-50 rounded-lg" />)}
        </div>
      )}

      {ratesError && !ratesLoading && tiers.length === KYO_FALLBACK_RATES.length && (
        <div className="px-5 pb-1 text-[11px] text-gray-400 flex items-center gap-1.5">
          <AlertCircle size={11} className="text-gray-300 shrink-0" />
          Showing standard KYo rates.
        </div>
      )}

      {!ratesLoading && !ratesError && tiers.length > 0 && expanded && (
        <div className="px-5 pb-4 border-t border-gray-50">
          <table className="w-full text-[12px] mt-3" data-testid="rates-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="text-left pb-2 font-semibold">Weight</th>
                <th className="text-right pb-2 font-semibold">Price (JMD)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tiers.map((tier, i) => {
                const w = tier.weight ?? tier.weight_lbs ?? tier.weight_lb ?? tier.lbs ?? tier.lb ?? tier.pounds;
                const label = (tier.min_weight !== undefined && tier.max_weight !== undefined)
                  ? `${tier.min_weight} – ${tier.max_weight} ${tier.unit || "lb"}`
                  : tier.weight_range || tier.range || tier.label
                    || (w !== undefined ? `${w} lb${Number(w) !== 1 ? "s" : ""}` : `Tier ${i + 1}`);

                /* Handle numeric or string prices like "J$1,000.00" or "600.00" */
                const rawRate = tier.price ?? tier.rate ?? tier.amount ?? tier.cost ?? tier.Price ?? 0;
                const numRate = parseFloat(String(rawRate).replace(/[^0-9.]/g, "")) || 0;
                return (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-2 text-gray-700">{label}</td>
                    <td className="py-2 text-right font-semibold text-gray-900">
                      J${numRate.toLocaleString("en-JM", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Invoice helpers ── */
function fmtInvoiceDate(raw) {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "2-digit",
    });
  } catch { return raw; }
}

function fmtAmount(amount, currency) {
  const num = parseFloat(amount) || 0;
  const cur = (currency || "JMD").toUpperCase();
  return `${cur} ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function InvoiceStatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  let cls, label;
  if (s === "paid")          { cls = "bg-green-50 text-green-700 border-green-100";  label = "Paid"; }
  else if (s === "overdue")  { cls = "bg-orange-50 text-orange-700 border-orange-100"; label = "Overdue"; }
  else if (s === "unpaid")   { cls = "bg-red-50 text-red-600 border-red-100";       label = "Unpaid"; }
  else                       { cls = "bg-gray-100 text-gray-600 border-gray-200";   label = status || "Unknown"; }
  return (
    <span className={`text-xs px-2.5 py-0.5 rounded-full border font-medium capitalize whitespace-nowrap ${cls}`}>
      {label}
    </span>
  );
}

function SurchargeTooltip({ base, surcharge, currency }) {
  const [open, setOpen] = React.useState(false);
  const cur = (currency || "JMD").toUpperCase();
  const fmt = (n) => `${cur} ${parseFloat(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <span className="relative inline-flex items-center ml-1.5" data-testid="surcharge-tooltip">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        className="text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Show surcharge breakdown"
      >
        <Info size={12} />
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 w-52 bg-gray-900 text-white text-[11px] rounded-xl p-3 shadow-xl pointer-events-none">
          <p className="font-semibold mb-1.5 text-white/80 uppercase tracking-wide text-[10px]">Amount breakdown</p>
          <div className="flex justify-between"><span>Base amount</span><span className="font-mono">{fmt(base)}</span></div>
          <div className="flex justify-between mt-1 text-orange-300"><span>Signature surcharge</span><span className="font-mono">+{fmt(surcharge)}</span></div>
          <div className="border-t border-white/20 mt-1.5 pt-1.5 flex justify-between font-semibold"><span>Total</span><span className="font-mono">{fmt((parseFloat(base) || 0) + (parseFloat(surcharge) || 0))}</span></div>
        </div>
      )}
    </span>
  );
}

/* ── Invoices empty state ── */
function EmptyInvoices() {
  return (
    <div className="flex flex-col items-center text-center py-14 px-6" data-testid="invoices-empty-state">
      <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-4">
        <Receipt size={28} className="text-gray-300" />
      </div>
      <h3 className="text-[15px] font-semibold text-gray-700 mb-1">No invoices yet</h3>
      <p className="text-[13px] text-gray-400 max-w-[260px] leading-relaxed">
        Invoices will appear here once KYo processes your first shipment.
      </p>
    </div>
  );
}

/* ── Invoices Section ── */
function InvoicesSection({ invoices, loading, error, onRefresh, identifier }) {
  const [statusFilter, setStatusFilter]     = React.useState("all");
  const [downloadStates, setDownloadStates] = React.useState({});   // { id: 'loading'|'error'|null }
  const [payStates, setPayStates]           = React.useState({});   // { id: { loading, error } }
  const [alertDismissed, setAlertDismissed] = React.useState(false);

  const parseInvoices = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    // New partner API returns { items: [...], total: N }
    // Return the array even when empty so "No invoices yet" renders correctly
    const candidates = [raw.items, raw.invoices, raw.data, raw.results, raw.list];
    for (const c of candidates) { if (Array.isArray(c)) return c; }
    return [];
  };

  const allInvoices = parseInvoices(invoices);
  const overdueCount = allInvoices.filter((inv) => (inv.status || "").toLowerCase() === "overdue").length;
  const showOverdueAlert = overdueCount > 0 && !alertDismissed;

  const STATUS_OPTIONS = [
    { value: "all",     label: "All statuses" },
    { value: "paid",    label: "Paid" },
    { value: "unpaid",  label: "Unpaid" },
    { value: "overdue", label: "Overdue" },
  ];

  const filtered = statusFilter === "all"
    ? allInvoices
    : allInvoices.filter((inv) => (inv.status || "").toLowerCase() === statusFilter);

  const getAuthHeaders = () => {
    try { const a = JSON.parse(localStorage.getItem("kyo_auth") || "{}"); return a.token ? { Authorization: `Bearer ${a.token}` } : {}; }
    catch { return {}; }
  };

  const KANAL_PUBLIC_PDF = (uuid) =>
    `https://kanal.cyrusfieldtech.com/api/public/invoices/${uuid}/pdf`;

  const downloadPDF = async (inv) => {
    const invoiceId  = inv.id || inv.invoice_id || inv.uuid;
    const invoiceNum = inv.invoice_number || invoiceId || "invoice";
    if (!invoiceId) return;
    setDownloadStates((s) => ({ ...s, [invoiceId]: "loading" }));
    try {
      const resp = await fetch(KANAL_PUBLIC_PDF(invoiceId));
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url  = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `${invoiceNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDownloadStates((s) => ({ ...s, [invoiceId]: null }));
    } catch {
      setDownloadStates((s) => ({ ...s, [invoiceId]: "error" }));
      setTimeout(() => setDownloadStates((s) => ({ ...s, [invoiceId]: null })), 3000);
    }
  };

  const viewPDF = (inv) => {
    const invoiceId = inv.id || inv.invoice_id || inv.uuid;
    if (!invoiceId) return;
    window.open(KANAL_PUBLIC_PDF(invoiceId), "_blank", "noopener,noreferrer");
  };

  const payNow = async (inv) => {
    const invoiceId = inv.id || inv.invoice_id || inv.uuid;
    if (!invoiceId) return;
    setPayStates((s) => ({ ...s, [invoiceId]: { loading: true, error: null } }));
    try {
      const { data } = await axios.post(
        `${API}/dashboard/invoices/${invoiceId}/pay`,
        null,
        { headers: getAuthHeaders() },
      );
      // Extract the redirect URL from the Kanal response
      const redirectUrl =
        data?.url            ||
        data?.checkout_url   ||
        data?.redirect_url   ||
        data?.payment_url    ||
        data?.link           ||
        null;
      if (redirectUrl) {
        setPayStates((s) => ({ ...s, [invoiceId]: { loading: false, error: null } }));
        window.location.href = redirectUrl;
      } else {
        throw new Error("No payment URL returned.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Payment could not be started. Please try again.";
      setPayStates((s) => ({ ...s, [invoiceId]: { loading: false, error: msg } }));
    }
  };

  return (
    <div className="mt-6" data-testid="invoices-section">

      {/* ── Overdue alert banner ── */}
      {showOverdueAlert && (
        <div
          className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-2xl px-5 py-4 mb-4"
          data-testid="overdue-alert-banner"
        >
          <AlertTriangle size={16} className="text-orange-500 mt-0.5 shrink-0" />
          <p className="text-[13px] text-orange-800 leading-relaxed flex-1">
            <span className="font-semibold">
              {overdueCount} overdue invoice{overdueCount > 1 ? "s" : ""}
            </span>
            {" "}— please settle {overdueCount > 1 ? "these" : "this"} to avoid delays on future shipments.
          </p>
          <button
            onClick={() => setAlertDismissed(true)}
            className="text-orange-400 hover:text-orange-600 transition-colors shrink-0 mt-0.5"
            aria-label="Dismiss overdue alert"
            data-testid="dismiss-overdue-alert"
          >
            <XIcon size={15} />
          </button>
        </div>
      )}

      {/* ── Section header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
            <Receipt size={15} className="text-[#ED2E24]" />
          </div>
          <h2 className="font-semibold text-gray-800 text-[14px]">Invoices</h2>
          {allInvoices.length > 0 && (
            <span className="bg-[#ED2E24]/10 text-[#ED2E24] text-xs font-bold px-2 py-0.5 rounded-full">
              {allInvoices.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {allInvoices.length > 0 && (
            <div className="relative" data-testid="invoice-status-filter">
              <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-8 pr-8 py-2 text-[12px] font-medium text-gray-600 border border-gray-200 rounded-xl bg-white outline-none focus:border-[#ED2E24] transition-colors appearance-none cursor-pointer hover:border-gray-300"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <ChevronDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-gray-300 hover:text-[#ED2E24] transition-colors disabled:opacity-40"
            title="Refresh invoices"
            data-testid="refresh-invoices-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm" data-testid="invoices-table-container">
        {loading ? (
          <div className="p-6 space-y-3 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex justify-between gap-4">
                <div className="h-4 bg-gray-100 rounded w-1/4" />
                <div className="h-4 bg-gray-100 rounded w-1/5" />
                <div className="h-4 bg-gray-50 rounded w-1/6" />
                <div className="h-5 bg-gray-100 rounded-full w-16" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center gap-2.5 px-6 py-5 text-red-500 text-[13px]" data-testid="invoices-error">
            <AlertCircle size={15} className="shrink-0" />
            <span>{error}</span>
          </div>
        ) : allInvoices.length === 0 ? (
          <EmptyInvoices />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-10 px-6 text-center" data-testid="invoices-filter-empty">
            <p className="text-[13px] text-gray-400">No <span className="font-semibold capitalize">{statusFilter}</span> invoices found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="invoices-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 font-semibold text-[11px] uppercase tracking-widest text-gray-400">Invoice #</th>
                  <th className="text-right px-5 py-3.5 font-semibold text-[11px] uppercase tracking-widest text-gray-400">Amount</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-[11px] uppercase tracking-widest text-gray-400 hidden sm:table-cell">Due Date</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-[11px] uppercase tracking-widest text-gray-400">Status</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-[11px] uppercase tracking-widest text-gray-400 hidden md:table-cell">Notes</th>
                  <th className="px-5 py-3.5 text-right font-semibold text-[11px] uppercase tracking-widest text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((inv, i) => {
                  const hasSurcharge = inv.signature_surcharge != null && parseFloat(inv.signature_surcharge) > 0;
                  const invoiceId    = inv.id || inv.invoice_id || inv.uuid;
                  return (
                    <tr
                      key={invoiceId || inv.invoice_number || i}
                      className="hover:bg-gray-50/60 transition-colors"
                      data-testid={`invoice-row-${i}`}
                    >
                      {/* Invoice # — with due date as second line on mobile */}
                      <td className="px-5 py-4">
                        <span className="font-mono font-semibold text-gray-800 whitespace-nowrap block">
                          {inv.invoice_number || `INV-${String(i + 1).padStart(4, "0")}`}
                        </span>
                        {inv.due_date && (
                          <span className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1 sm:hidden">
                            <Calendar size={10} />
                            {fmtInvoiceDate(inv.due_date)}
                          </span>
                        )}
                      </td>
                      {/* Amount */}
                      <td className="px-5 py-4 text-right font-semibold text-gray-900 whitespace-nowrap">
                        <span className="inline-flex items-center justify-end">
                          {fmtAmount(inv.amount, inv.currency)}
                          {hasSurcharge && (
                            <SurchargeTooltip
                              base={inv.base_amount ?? (parseFloat(inv.amount) - parseFloat(inv.signature_surcharge))}
                              surcharge={inv.signature_surcharge}
                              currency={inv.currency}
                            />
                          )}
                        </span>
                      </td>
                      {/* Due Date — desktop only */}
                      <td className="px-5 py-4 text-gray-600 whitespace-nowrap hidden sm:table-cell">
                        {fmtInvoiceDate(inv.due_date)}
                      </td>
                      {/* Status */}
                      <td className="px-5 py-4">
                        <InvoiceStatusBadge status={inv.status} />
                      </td>
                      {/* Notes — md+ only */}
                      <td className="px-5 py-4 text-gray-500 max-w-[180px] truncate hidden md:table-cell" title={inv.notes || ""}>
                        {inv.notes || <span className="text-gray-300">—</span>}
                      </td>
                      {/* Actions: Pay Now (if unpaid/open) + Download */}
                      <td className="px-3 py-3 text-right">
                        {(() => {
                          const st      = (inv.status || "").toLowerCase();
                          const payable = st === "unpaid" || st === "open";
                          const ps      = payStates[invoiceId];
                          const dlState = downloadStates[invoiceId];
                          return (
                            <div className="flex flex-col items-end gap-1.5">
                              {/* Pay Now button */}
                              {payable && (
                                <div className="flex flex-col items-end gap-1">
                                  <button
                                    onClick={() => payNow(inv)}
                                    disabled={ps?.loading}
                                    data-testid={`pay-now-${i}`}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-[#ED2E24] text-white hover:bg-[#c9231a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap shadow-sm"
                                  >
                                    {ps?.loading
                                      ? <><Loader2 size={10} className="animate-spin" />Processing…</>
                                      : "Pay Now"}
                                  </button>
                                  {ps?.error && (
                                    <p
                                      className="text-[10px] text-red-500 text-right leading-tight max-w-[130px]"
                                      data-testid={`pay-error-${i}`}
                                    >
                                      {ps.error}
                                    </p>
                                  )}
                                </div>
                              )}
                              {/* View + Download buttons */}
                              <div className="flex items-center gap-1 justify-end">
                                {invoiceId && (
                                  <button
                                    onClick={() => viewPDF(inv)}
                                    title="View PDF"
                                    data-testid={`view-invoice-${i}`}
                                    className="p-1.5 rounded-lg text-gray-300 hover:text-[#ED2E24] hover:bg-red-50 transition-colors"
                                  >
                                    <Eye size={14} />
                                  </button>
                                )}
                                {invoiceId ? (
                                  <button
                                    onClick={() => downloadPDF(inv)}
                                    disabled={dlState === "loading"}
                                    title={dlState === "error" ? "Download failed — try again" : "Download PDF"}
                                    data-testid={`download-invoice-${i}`}
                                    className={`p-1.5 rounded-lg transition-colors ${
                                      dlState === "error"   ? "text-red-400 hover:text-red-600"
                                      : dlState === "loading" ? "text-gray-300"
                                      : "text-gray-300 hover:text-[#ED2E24] hover:bg-red-50"
                                    }`}
                                  >
                                    {dlState === "loading"
                                      ? <Loader2 size={14} className="animate-spin" />
                                      : <Download size={14} />}
                                  </button>
                                ) : (
                                  <span className="text-gray-200 p-1.5" title="Download unavailable">
                                    <Download size={14} />
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Dashboard Page
══════════════════════════════════════════════════════ */
export default function DashboardPage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const customerNumber = getCustomerNumber();

  const [profile, setProfile] = useState(null);
  const [packages, setPackages] = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [packagesError, setPackagesError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [localHistory, setLocalHistory] = useState(readLocalHistory);

  /* Change password */
  const [showChangePw, setShowChangePw] = useState(false);

  /* Shipping rates */
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [ratesError, setRatesError] = useState(null);

  /* Invoices */
  const [invoices, setInvoices] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState(null);

  // Guard — redirect if not authenticated
  useEffect(() => {
    if (!auth?.token) navigate("/signin");
  }, []); // eslint-disable-line

  const fetchProfile = useCallback(async () => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      const { data } = await axios.get(`${API}/dashboard/me`, { headers: getAuthHeaders() });
      // Audit: log every field so we can identify the correct customer number field
      console.log("[KYo profile] full response:", JSON.stringify(data, null, 2));
      setProfile(data);
      // Chain invoice fetch immediately after profile resolves so we use
      // the live API customer_number, not the stale localStorage value.
      fetchInvoicesWithProfile(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setProfileError("Session expired. Please sign in again to see your account details.");
      } else {
        setProfileError("Could not load account details.");
      }
      // Profile failed — still attempt invoices with best available identifier
      fetchInvoicesWithProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []); // eslint-disable-line

  /**
   * Resolve the customer identifier from profile data first, then localStorage,
   * then auth email — in that order of reliability.
   * Accepts an optional profileData argument so it can be called right after
   * fetchProfile resolves (before React has re-rendered with the new profile state).
   */
  const fetchInvoicesWithProfile = useCallback(async (profileData) => {
    // Priority: live profile API fields → localStorage → auth email
    const identifier =
      profileData?.customer_number ||
      profileData?.kanal_id        ||
      profileData?.code            ||
      profileData?.id              ||
      getCustomerNumber()          ||
      getAuth()?.email             ||
      "";

    console.log("Invoice identifier:", identifier);

    if (!identifier) {
      setInvoicesError("Customer identifier not available — please sign in again.");
      setInvoicesLoading(false);
      return;
    }
    setInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const { data } = await axios.get(`${API}/dashboard/invoices`, {
        headers: getAuthHeaders(),
        params: { identifier },
      });
      console.log("[KYo invoices] raw response:", JSON.stringify(data, null, 2));
      setInvoices(data);
    } catch (err) {
      console.log("[KYo invoices] error:", err?.response?.status, err?.response?.data);
      if (err?.response?.status === 401) {
        setInvoicesError("Session expired. Please sign in again.");
      } else {
        setInvoicesError(err?.response?.data?.detail || "Could not load invoices.");
      }
    } finally {
      setInvoicesLoading(false);
    }
  }, []); // eslint-disable-line

  // fetchInvoices (for the Refresh button) uses the current profile state
  const fetchInvoices = useCallback(async () => {
    fetchInvoicesWithProfile(profile);
  }, [profile, fetchInvoicesWithProfile]); // eslint-disable-line

  const fetchPackages = useCallback(async () => {
    setPackagesLoading(true);
    setPackagesError(null);
    try {
      const { data } = await axios.get(`${API}/dashboard/packages`, { headers: getAuthHeaders() });
      const list = Array.isArray(data)
        ? data
        : data?.packages ?? data?.data ?? data?.items ?? data?.results ?? [];
      setPackages(list);
      syncApiTNsToLocalHistory(list);
      setLocalHistory(readLocalHistory());
    } catch (err) {
      if (err?.response?.status === 401) {
        setPackagesError("Session expired. Please sign in again to see your packages.");
      } else {
        setPackagesError("Could not load packages.");
      }
    } finally {
      setPackagesLoading(false);
    }
  }, []); // eslint-disable-line

  const fetchRates = useCallback(async () => {
    setRatesLoading(true);
    setRatesError(null);
    try {
      const { data } = await axios.get(`${API}/dashboard/rates`, { headers: getAuthHeaders() });
      console.log("[KYo rates] raw response:", JSON.stringify(data, null, 2));
      setRates(data);
    } catch (err) {
      console.log("[KYo rates] error:", err?.response?.status, err?.response?.data);
      setRatesError(err?.response?.data?.detail || "Could not load rates.");
    } finally {
      setRatesLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (auth?.token) {
      fetchProfile();   // also chains fetchInvoicesWithProfile on completion
      fetchPackages();
      fetchRates();
    }
  }, []); // eslint-disable-line

  const logout = () => {
    localStorage.removeItem("kyo_auth");
    navigate("/");
  };

  const goTrack = () => navigate("/");

  const displayName =
    profile?.name ||
    profile?.full_name ||
    profile?.first_name ||
    (profile?.first_name && profile?.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : null) ||
    auth?.email ||
    "";

  // Tracking numbers that are in localStorage but NOT in the API packages list
  const apiTNSet = new Set(
    packages.map(p => p.tracking_number || p.trackingNumber || p.number).filter(Boolean)
  );
  const localOnlyHistory = localHistory.filter(tn => !apiTNSet.has(tn));

  return (
    <div className="min-h-screen bg-[#f4f4f4]" data-testid="dashboard-page">

      {/* ── Top nav ── */}
      <header className="bg-[#212327] shadow-lg">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-6 h-[64px] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate("/")}
              className="text-white/50 hover:text-white transition-colors p-1"
              title="Back to KYo Group"
              data-testid="dashboard-home-btn"
            >
              <Home size={18} />
            </button>
            <span className="text-white/25 select-none">/</span>
            <span className="font-semibold text-white text-[14px] tracking-wide">My Dashboard</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-white/50 text-xs max-w-[180px] truncate">
              {auth?.email}
            </span>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[12px] font-medium px-3 py-1.5 rounded-md transition-colors"
              data-testid="dashboard-logout-btn"
            >
              <LogOut size={13} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-5 lg:px-6 py-8">

        {/* ── Welcome banner ── */}
        <div
          className="bg-[#212327] text-white rounded-2xl px-6 py-5 mb-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          data-testid="dashboard-welcome-banner"
        >
          <div>
            <p className="text-white/40 text-[11px] font-semibold uppercase tracking-widest">Welcome back</p>
            <h1 className="text-xl font-bold mt-1.5 truncate max-w-sm" data-testid="dashboard-display-name">
              {displayName}
            </h1>
          </div>
          {customerNumber && (
            <div className="shrink-0 sm:text-right bg-white/5 rounded-xl px-5 py-3">
              <p className="text-white/40 text-[11px] font-semibold uppercase tracking-widest">Customer ID</p>
              <p
                className="text-[#ED2E24] font-bold font-mono text-xl tracking-widest mt-1"
                data-testid="dashboard-customer-id"
              >
                {customerNumber}
              </p>
            </div>
          )}
        </div>

        {/* ── Warehouse shipping address ── */}
        <WarehouseCard
          profile={profile}
          warehouse={profile}
          warehouseLoading={profileLoading}
          customerNumber={customerNumber}
        />

        {/* ── Main grid ── */}
        <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-start">

          {/* ── Left column: Account Details + Shipping Rates ── */}
          <div className="flex flex-col">

          {/* Account Details card */}
          <div
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            data-testid="account-details-card"
          >
            {/* Card header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center">
                  <User size={15} className="text-[#ED2E24]" />
                </div>
                <h2 className="font-semibold text-gray-800 text-[14px]">Account Details</h2>
              </div>
              <button
                onClick={fetchProfile}
                disabled={profileLoading}
                className="text-gray-300 hover:text-[#ED2E24] transition-colors disabled:opacity-40"
                title="Refresh account details"
                data-testid="refresh-profile-btn"
              >
                <RefreshCw size={14} className={profileLoading ? "animate-spin" : ""} />
              </button>
            </div>

            <div className="px-5 py-2">
              {profileLoading ? (
                <div className="py-3">
                  <Skeleton lines={5} />
                </div>
              ) : profileError ? (
                <div className="flex items-center gap-2 text-red-500 text-sm py-5">
                  <AlertCircle size={15} className="shrink-0" />
                  <span>{profileError}</span>
                </div>
              ) : profile ? (
                <>
                  <DetailRow icon={User}      label="Full Name"       value={profile.name || profile.full_name || (profile.first_name ? `${profile.first_name} ${profile.last_name || ""}`.trim() : null)} />
                  <DetailRow icon={Mail}      label="Email"           value={profile.email} />
                  <DetailRow icon={Phone}     label="Phone"           value={profile.phone} />
                  <DetailRow icon={MapPin}    label="Address"         value={profile.address} />
                  <DetailRow icon={Globe}     label="Country"         value={profile.country} />
                  <DetailRow icon={CreditCard} label="Customer Number" value={profile.customer_number || customerNumber} />
                  <DetailRow icon={CreditCard} label="TRN"            value={profile.trn ? `•••••${String(profile.trn).slice(-4)}` : null} />
                  <DetailRow icon={Calendar}  label="Member Since"    value={formatDate(profile.created_at || profile.createdAt)} />
                </>
              ) : null}
            </div>

            {/* Change Password */}
            {!profileLoading && !profileError && (
              <div className="px-5 pb-4 border-t border-gray-50 pt-3">
                {!showChangePw ? (
                  <button
                    onClick={() => setShowChangePw(true)}
                    className="flex items-center gap-2 text-[13px] text-gray-500 hover:text-[#ED2E24] font-medium transition-colors w-full text-left"
                    data-testid="change-password-btn"
                  >
                    <Lock size={13} />
                    Change Password
                  </button>
                ) : (
                  <div>
                    <p className="text-[12px] font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                      <Lock size={12} />Change Password
                    </p>
                    <ChangePasswordForm onClose={() => setShowChangePw(false)} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Shipping Rates card */}
          <ShippingRatesCard rates={rates} ratesLoading={ratesLoading} ratesError={ratesError} />

          </div>{/* end left column */}

          {/* ── Right column: Packages section ── */}
          <div data-testid="packages-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center">
                  <Package size={15} className="text-[#ED2E24]" />
                </div>
                <h2 className="font-semibold text-gray-800 text-[14px]">My Packages</h2>
                {packages.length > 0 && (
                  <span className="bg-[#ED2E24]/10 text-[#ED2E24] text-xs font-bold px-2 py-0.5 rounded-full">
                    {packages.length}
                  </span>
                )}
              </div>
              <button
                onClick={fetchPackages}
                disabled={packagesLoading}
                className="text-gray-300 hover:text-[#ED2E24] transition-colors disabled:opacity-40"
                title="Refresh packages"
                data-testid="refresh-packages-btn"
              >
                <RefreshCw size={14} className={packagesLoading ? "animate-spin" : ""} />
              </button>
            </div>

            {/* Quick Track bar */}
            <QuickTrack />

            {packagesLoading ? (
              <PackageSkeleton />
            ) : packagesError ? (
              <>
                <div className="bg-white rounded-2xl border border-gray-100 p-6 flex items-center gap-3 text-red-500 text-sm">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{packagesError}</span>
                </div>
                {localOnlyHistory.length > 0 && (
                  <div className="mt-6" data-testid="recently-tracked-section">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Clock size={11} /> Recently Tracked
                    </p>
                    <div className="space-y-2">
                      {localOnlyHistory.map(tn => (
                        <LocallyTrackedCard key={tn} tn={tn} expandedId={expandedId} setExpandedId={setExpandedId} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : packages.length === 0 ? (
              <>
                <EmptyPackages onTrack={goTrack} />
                {localOnlyHistory.length > 0 && (
                  <div className="mt-6" data-testid="recently-tracked-section">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Clock size={11} /> Recently Tracked
                    </p>
                    <div className="space-y-2">
                      {localOnlyHistory.map(tn => (
                        <LocallyTrackedCard key={tn} tn={tn} expandedId={expandedId} setExpandedId={setExpandedId} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-3" data-testid="packages-list">
                  {packages.map((pkg, idx) => {
                    const tn = pkg.tracking_number || pkg.trackingNumber || pkg.number || pkg.id;
                    const from = pkg.origin || pkg.from || pkg.source;
                    const to = pkg.destination || pkg.to;
                    const status = pkg.status || pkg.package_status || pkg.state;
                    const date = pkg.created_at || pkg.createdAt || pkg.date || pkg.shipped_at;
                    const desc = pkg.description || pkg.item_description || pkg.contents;
                    const isExpanded = expandedId === (tn || idx);

                    return (
                      <div
                        key={tn || idx}
                        className={`bg-white rounded-xl border transition-all duration-200 ${
                          isExpanded ? "border-[#ED2E24]/30 shadow-md" : "border-gray-100 hover:border-gray-200 hover:shadow-sm"
                        }`}
                        data-testid="package-card"
                      >
                        <button
                          className="w-full text-left px-5 py-4 group"
                          onClick={() => setExpandedId(isExpanded ? null : (tn || idx))}
                          data-testid={`package-expand-btn-${tn || idx}`}
                          aria-expanded={isExpanded}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <p className={`font-mono text-sm font-bold transition-colors ${isExpanded ? "text-[#ED2E24]" : "text-gray-900 group-hover:text-[#ED2E24]"}`}>
                                {tn || `PKG-${idx + 1}`}
                              </p>
                              {(from || to) && (
                                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                  {from && <span>{from}</span>}
                                  {from && to && <span className="text-gray-300">→</span>}
                                  {to && <span>{to}</span>}
                                </p>
                              )}
                              {desc && (
                                <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[300px]">{desc}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <div className="flex flex-col items-end gap-1.5">
                                {status && <StatusBadge status={status} />}
                                {date && <p className="text-[11px] text-gray-400">{formatDate(date)}</p>}
                              </div>
                              <span className={`text-gray-300 transition-transform duration-200 ${isExpanded ? "rotate-180 text-[#ED2E24]" : ""}`}>
                                <ChevronDown size={16} />
                              </span>
                            </div>
                          </div>
                          {pkg.weight && (
                            <p className="text-[11px] text-gray-400 mt-2 text-left">Weight: {pkg.weight}</p>
                          )}
                        </button>

                        {isExpanded && tn && (
                          <div className="px-5 pb-5" data-testid={`tracking-timeline-${tn}`}>
                            <InlineTimeline tn={tn} />
                          </div>
                        )}
                        {isExpanded && !tn && (
                          <div className="px-5 pb-4 text-xs text-gray-400 italic border-t border-gray-100 pt-3">
                            No tracking number available for this package.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Recently Tracked — local history items not in API list */}
                {localOnlyHistory.length > 0 && (
                  <div className="mt-6" data-testid="recently-tracked-section">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Clock size={11} /> Recently Tracked
                    </p>
                    <div className="space-y-2">
                      {localOnlyHistory.map(tn => (
                        <LocallyTrackedCard key={tn} tn={tn} expandedId={expandedId} setExpandedId={setExpandedId} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>{/* end main grid */}

        {/* ── Invoices section (full-width below grid) ── */}
        <InvoicesSection
          invoices={invoices}
          loading={invoicesLoading}
          error={invoicesError}
          onRefresh={fetchInvoices}
          identifier={
            profile?.customer_number ||
            profile?.kanal_id        ||
            profile?.code            ||
            profile?.id              ||
            getCustomerNumber()      ||
            getAuth()?.email         ||
            ""
          }
        />

      </main>
    </div>
  );
}
